import React, { useMemo } from 'react';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import ms from 'milsymbol';
import parse from 'html-react-parser';
import { Faction, AffiliationType, getAffiliationSidcDigit } from '../../types/orbat';
import { buildSidc } from '../unit/SidcBuilder/sidcOptions';

interface FactionCardProps {
    faction: Faction;
    affiliations: AffiliationType[];
    isSelected: boolean;
    onSelect: (factionId: string) => void;
    onEdit: (faction: Faction) => void;
    onDelete: (faction: Faction) => void;
}

/**
 * Card component displaying a faction with its military symbol.
 */
const FactionCard: React.FC<FactionCardProps> = ({
    faction,
    affiliations,
    isSelected,
    onSelect,
    onEdit,
    onDelete,
}) => {
    // Find the affiliation object for this faction
    const affiliation = useMemo(() => {
        return affiliations.find((a) => a.id === faction.affiliation_id);
    }, [affiliations, faction.affiliation_id]);

    // Generate a representative military symbol for the faction based on affiliation
    // Uses APP-6D standard for SIDC generation
    const symbolSvg = useMemo(() => {
        // Get the correct SIDC digit based on affiliation name
        const identityDigit = affiliation
            ? getAffiliationSidcDigit(affiliation.name)
            : '3'; // Default to friendly

        // Build APP-6D compliant SIDC:
        // - Identity: from affiliation
        // - Symbol Set: 10 (Land Unit)
        // - Status: 0 (Present)
        // - HQTFD: 2 (Headquarters)
        // - Echelon: 00 (Unspecified)
        // - Unit Type: 1100000000 (Command & Control)
        const sidc = buildSidc(identityDigit, '10', '0', '2', '00', '1100000000');

        const symbol = new ms.Symbol(sidc, { size: 40 });
        return symbol.asSVG();
    }, [affiliation]);

    const handleClick = () => {
        onSelect(faction.id);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit(faction);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(faction);
    };

    // Get affiliation label for display
    const affiliationLabel = affiliation?.name || 'Unknown';

    return (
        <Card
            className={`faction-card ${isSelected ? 'faction-card--selected' : ''}`}
            onClick={handleClick}
        >
            <div className="faction-card__body">
                <div className="faction-card__symbol">
                    {parse(symbolSvg)}
                </div>
                <div className="faction-card__info">
                    <h6>{faction.name}</h6>
                    <small>{affiliationLabel}</small>
                </div>
                <div className="faction-card__actions">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleEdit}
                        title="Edit faction"
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDelete}
                        title="Delete faction"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            </div>
        </Card>
    );
};

export default FactionCard;
